'use client';

import React from 'react';
import { Menu, Car, Phone, Mail } from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { safeHref } from '@/lib/storefront/safe-url';
import { containerClass, sectionStyle } from '@/lib/storefront/section-style';
import { resolveContactField, useStorefrontData } from '@/lib/storefront/storefront-data';
import { apiAsset } from '@/config';

export { containerClass, sectionStyle };

function useAgencyBrand(content: Record<string, unknown>) {
  const { agency } = useStorefrontData();
  const logoText = resolveContactField(content.logoText, agency?.name) || 'Agency';
  const logoUrl = resolveContactField(content.logoUrl, agency?.logoUrl);
  const phone = resolveContactField(content.phone, agency?.phone);
  const email = resolveContactField(content.email, agency?.email);
  return { logoText, logoUrl, phone, email };
}

function Logo({
  text,
  logoUrl,
  color = 'text-slate-900',
}: {
  text: string;
  logoUrl?: string;
  color?: string;
}) {
  const src = logoUrl ? apiAsset(logoUrl) : '';
  return (
    <a href="/" className={`flex items-center gap-2 text-xl font-bold ${color}`} aria-label={`${text} - Home`}>
      {src ? (
        <img src={src} alt="" className="h-8 w-auto max-w-[9rem] object-contain" />
      ) : (
        <Car className="h-6 w-6 text-brand" />
      )}
      <span>{text}</span>
    </a>
  );
}

export function ClassicHeader({ content, style }: SectionProps) {
  const {
    navItems = [
      { label: 'Vehicles', href: '/vehicles' },
      { label: 'How it Works', href: '/services' },
      { label: 'Pricing', href: '/deals' },
      { label: 'Contact', href: '/contact' },
    ],
    ctaText = 'Book Now',
    ctaHref = '/vehicles',
    showContact = true,
  } = content;
  const { logoText, logoUrl, phone, email } = useAgencyBrand(content);

  return (
    <header className="w-full border-b border-slate-200 bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="flex h-16 items-center justify-between gap-6">
          <Logo text={logoText} logoUrl={logoUrl} />

          <nav className="hidden items-center gap-8 md:flex">
            {navItems.map((item: { label?: string; href?: string }, i: number) => (
              <a key={i} href={safeHref(item.href)} className="text-sm font-medium text-slate-600 transition-colors hover:text-brand">
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-5 md:flex">
            {showContact && (
              <div className="flex items-center gap-4 text-xs text-slate-500">
                {phone && (
                  <a href={`tel:${String(phone).replace(/\s/g, '')}`} className="flex items-center gap-1.5 hover:text-brand">
                    <Phone className="h-3.5 w-3.5" />
                    {phone}
                  </a>
                )}
                {email && (
                  <a href={`mailto:${email}`} className="flex items-center gap-1.5 hover:text-brand">
                    <Mail className="h-3.5 w-3.5" />
                    {email}
                  </a>
                )}
              </div>
            )}
            <a href={safeHref(ctaHref)} className="rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand/90">
              {ctaText}
            </a>
          </div>

          <button className="rounded-lg p-2 text-slate-600 md:hidden" aria-label="Toggle menu">
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>
    </header>
  );
}

export function ModernHeader({ content, style }: SectionProps) {
  const {
    navItems = [
      { label: 'Fleet', href: '/vehicles' },
      { label: 'Features', href: '/services' },
      { label: 'Testimonials', href: '/about' },
      { label: 'Pricing', href: '/deals' },
    ],
    ctaText = 'Get Started',
    ctaHref = '/contact',
  } = content;
  const { logoText, logoUrl } = useAgencyBrand(content);

  return (
    <header className="w-full border-b border-slate-100 bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="flex h-[72px] items-center justify-between gap-4">
          <Logo text={logoText} logoUrl={logoUrl} />

          <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex">
            {navItems.map((item: { label?: string; href?: string }, i: number) => (
              <a key={i} href={safeHref(item.href)} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-brand">
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href={safeHref(ctaHref)} className="hidden rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 sm:inline-flex">
              {ctaText}
            </a>
            <button className="rounded-lg p-2 text-slate-600 lg:hidden" aria-label="Toggle menu">
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export function MinimalHeader({ content, style }: SectionProps) {
  const {
    navItems = [
      { label: 'Home', href: '/' },
      { label: 'Vehicles', href: '/vehicles' },
      { label: 'About', href: '/about' },
      { label: 'Contact', href: '/contact' },
    ],
    ctaText = 'Reserve',
    ctaHref = '/vehicles',
    transparent = false,
  } = content;
  const { logoText, logoUrl } = useAgencyBrand(content);

  return (
    <header
      className={`w-full border-b ${transparent ? 'border-transparent bg-transparent' : 'border-slate-200 bg-white'}`}
      style={sectionStyle(style)}
    >
      <div className={containerClass(style)}>
        <div className="flex h-16 items-center justify-between">
          <Logo text={logoText} logoUrl={logoUrl} />
          <nav className="hidden items-center gap-6 md:flex">
            {navItems.map((item: { label?: string; href?: string }, i: number) => (
              <a key={i} href={safeHref(item.href)} className="text-sm font-medium text-slate-700 hover:text-brand">
                {item.label}
              </a>
            ))}
          </nav>
          <a href={safeHref(ctaHref)} className="hidden rounded-xl border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 md:inline-flex">
            {ctaText}
          </a>
          <button className="rounded-lg p-2 text-slate-600 md:hidden" aria-label="Toggle menu">
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>
    </header>
  );
}

export function TwoRowHeader({ content, style }: SectionProps) {
  const {
    topBar = {
      text: 'Free delivery on orders over 500 DH',
      linkText: 'Shop now',
      linkHref: '/vehicles',
      backgroundColor: '#0D1117',
      textColor: '#ffffff',
    },
    navItems = [
      { label: 'Vehicles', href: '/vehicles' },
      { label: 'Categories', href: '/vehicles' },
      { label: 'Deals', href: '/deals' },
      { label: 'Support', href: '/contact' },
    ],
    ctaText = 'Book Vehicle',
    ctaHref = '/vehicles',
  } = content;
  const { logoText, logoUrl } = useAgencyBrand(content);

  return (
    <header className="w-full" style={sectionStyle(style)}>
      <div className="flex w-full flex-wrap items-center justify-center gap-3 py-2 text-xs font-medium" style={{ backgroundColor: topBar.backgroundColor, color: topBar.textColor }}>
        <span>{topBar.text}</span>
        {topBar.linkText && topBar.linkHref && (
          <a href={topBar.linkHref} className="underline hover:no-underline" style={{ color: topBar.textColor }}>
            {topBar.linkText}
          </a>
        )}
      </div>

      <div className="border-b border-slate-200 bg-white">
        <div className={containerClass(style)}>
          <div className="flex h-16 items-center justify-between">
            <Logo text={logoText} logoUrl={logoUrl} />
            <nav className="hidden items-center gap-8 md:flex">
              {navItems.map((item: { label?: string; href?: string }, i: number) => (
                <a key={i} href={safeHref(item.href)} className="text-sm font-medium text-slate-600 hover:text-brand">
                  {item.label}
                </a>
              ))}
            </nav>
            <a href={safeHref(ctaHref)} className="hidden rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand/90 md:inline-flex">
              {ctaText}
            </a>
            <button className="rounded-lg p-2 text-slate-600 md:hidden" aria-label="Toggle menu">
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export const HEADER_VARIATIONS = {
  classic: ClassicHeader,
  modern: ModernHeader,
  minimal: MinimalHeader,
  two_row: TwoRowHeader,
} as const;

export type HeaderVariationKey = keyof typeof HEADER_VARIATIONS;
